import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { registerGuest, getHotelInfo } from './nexastayApi';
import countryCodesData from '../data/country-codes.json';
import {
  formatPhoneLengthLabel,
  normalizePhoneInputForCountry,
  validatePhoneForCountry,
} from '../utils/phoneValidation';
import { resolveGuestPhoneLocale } from './nexastayGuestLocale';

const inputSx = (accent, theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.9)',
    '& fieldset': { borderColor: `${accent}35` },
    '&:hover fieldset': { borderColor: `${accent}70` },
    '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const startAdornmentSx = { minWidth: 44 };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_MIN_LENGTH = 2;
const PASSWORD_MIN_LENGTH = 8;

function validateName(value) {
  const v = (value || '').trim();
  if (!v) return 'Name is required';
  if (v.length < NAME_MIN_LENGTH) return `Name must be at least ${NAME_MIN_LENGTH} characters`;
  return '';
}

function validatePhone(value, dialCode) {
  return validatePhoneForCountry(value, dialCode || '+91', {
    required: true,
    label: 'Phone',
  });
}

function validateEmail(value) {
  const v = (value || '').trim();
  if (!v) return 'Email is required';
  if (!EMAIL_REGEX.test(v)) return 'Enter a valid email address';
  return '';
}

function validatePassword(value) {
  if (!value) return 'Password is required';
  if (value.length < PASSWORD_MIN_LENGTH) return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  return '';
}

export default function NexaStayRegisterDialog({ open, onClose, onSignInClick, hotelId, onRegisterSuccess }) {
  const theme = useNexaStayTheme();
  const accent = theme.accent;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ name: '', phone: '', email: '', password: '' });
  const [touched, setTouched] = useState({ name: false, phone: false, email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const phoneCountryCodeAnchorRef = useRef(null);
  const countryCodes = useMemo(() => countryCodesData, []);
  const selectedPhoneCountryCode = useMemo(
    () =>
      countryCodes.find((cc) => cc.dialCode === phoneCountryCode) ||
      countryCodes.find((cc) => cc.code === 'IN') ||
      countryCodes[0],
    [countryCodes, phoneCountryCode]
  );

  useEffect(() => {
    if (!open) return;
    setName('');
    setPhone('');
    setEmail('');
    setPassword('');
    setErrors({ name: '', phone: '', email: '', password: '' });
    setTouched({ name: false, phone: false, email: false, password: false });
    setSubmitError('');
    setPhoneCountryCode('+91');

    if (!hotelId) return;
    let cancelled = false;
    getHotelInfo(hotelId)
      .then((hotel) => {
        if (cancelled) return;
        const locale = resolveGuestPhoneLocale({
          visitorCountry: hotel?.visitor_country,
          visitorCountryIso: hotel?.visitor_country_iso,
          hotelCountry: hotel?.country,
          countryCodes,
        });
        if (locale.dialCode) setPhoneCountryCode(locale.dialCode);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, hotelId, countryCodes]);

  const runValidation = () => ({
    name: validateName(name),
    phone: validatePhone(phone, phoneCountryCode),
    email: validateEmail(email),
    password: validatePassword(password),
  });

  const validation = runValidation();
  const isFormValid =
    !validation.name && !validation.phone && !validation.email && !validation.password;

  const setError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const msg =
      field === 'name'
        ? validateName(name)
        : field === 'phone'
          ? validatePhone(phone, phoneCountryCode)
          : field === 'email'
            ? validateEmail(email)
            : validatePassword(password);
    setError(field, msg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true, password: true });
    const nextErrors = runValidation();
    setErrors(nextErrors);
    if (nextErrors.name || nextErrors.phone || nextErrors.email || nextErrors.password) return;
    if (!hotelId) {
      setSubmitError('Hotel context missing. Please refresh and try again.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const data = await registerGuest(hotelId, {
        name: name.trim(),
        phone: normalizePhoneInputForCountry(phone, phoneCountryCode),
        country_code: phoneCountryCode,
        email: email.trim(),
        password,
      });
      onRegisterSuccess?.(data);
      onClose?.();
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.response?.data?.error || err.message || 'Registration failed.';
      setSubmitError(typeof msg === 'string' ? msg : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${accent}40`,
          boxShadow:
            theme.mode === 'dark'
              ? `0 24px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06), 0 0 40px ${accent}20`
              : `0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06), 0 0 40px ${accent}15`,
          bgcolor: theme.paper,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: `${accent}18`,
          borderBottom: `2px solid ${accent}40`,
          px: 3,
          pt: 2.5,
          pb: 2,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: theme.textSecondary,
            '&:hover': { color: theme.textPrimary, bgcolor: `${accent}18` },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}30`,
              color: accent,
              boxShadow: `0 4px 12px ${accent}40`,
            }}
          >
            <PersonAddIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 700, letterSpacing: '-0.02em' }}>
              Create account
            </Typography>
            <Typography variant="body2" sx={{ color: theme.textSecondary }}>
              Name, phone, email and password
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
        {submitError && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: theme.mode === 'dark' ? 'rgba(244,67,54,0.15)' : 'rgba(244,67,54,0.08)',
              color: '#f44336',
              fontSize: '0.875rem',
            }}
          >
            {submitError}
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Full name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (touched.name) setError('name', validateName(e.target.value));
              }}
              onBlur={handleBlur('name')}
              variant="outlined"
              placeholder="e.g. John Doe"
              autoComplete="name"
              error={!!errors.name}
              helperText={errors.name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={startAdornmentSx}>
                    <BadgeIcon sx={{ color: accent, fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx(accent, theme)}
            />
            <TextField
              fullWidth
              label="Phone"
              value={phone}
              onChange={(e) => {
                const next = normalizePhoneInputForCountry(e.target.value, phoneCountryCode);
                setPhone(next);
                if (touched.phone) setError('phone', validatePhone(next, phoneCountryCode));
              }}
              onBlur={handleBlur('phone')}
              variant="outlined"
              placeholder="Mobile number"
              autoComplete="tel"
              error={!!errors.phone}
              helperText={errors.phone || `Enter ${formatPhoneLengthLabel(phoneCountryCode)}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      ref={phoneCountryCodeAnchorRef}
                      onClick={() => setPhoneCountryCodeMenuOpen(true)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: 0.5,
                        px: 0.5,
                        mr: 0.5,
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        },
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                        {selectedPhoneCountryCode?.flag || '🇮🇳'}
                      </Typography>
                      <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {selectedPhoneCountryCode?.dialCode || phoneCountryCode}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: '0.7rem', color: theme.textSecondary }}>
                        ▼
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={inputSx(accent, theme)}
            />
            <Menu
              anchorEl={phoneCountryCodeAnchorRef.current}
              open={phoneCountryCodeMenuOpen}
              onClose={() => {
                setPhoneCountryCodeMenuOpen(false);
                setPhoneCountryCodeSearch('');
              }}
              PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
            >
              <Box sx={{ p: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search country code..."
                  value={phoneCountryCodeSearch}
                  onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                  sx={{ mb: 1 }}
                />
              </Box>
              {countryCodes
                .filter((c) => {
                  if (!phoneCountryCodeSearch) return true;
                  const q = phoneCountryCodeSearch.toLowerCase();
                  return (
                    c.name.toLowerCase().includes(q) ||
                    c.dialCode.includes(q) ||
                    c.code.toLowerCase().includes(q)
                  );
                })
                .map((c) => (
                  <MenuItem
                    key={c.code}
                    onClick={() => {
                      setPhoneCountryCode(c.dialCode);
                      setPhone(normalizePhoneInputForCountry(phone, c.dialCode));
                      if (touched.phone) setError('phone', validatePhone(phone, c.dialCode));
                      setPhoneCountryCodeMenuOpen(false);
                      setPhoneCountryCodeSearch('');
                    }}
                  >
                    <Typography component="span" sx={{ mr: 1 }}>{c.flag}</Typography>
                    {c.name} ({c.dialCode})
                  </MenuItem>
                ))}
            </Menu>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (touched.email) setError('email', validateEmail(e.target.value));
              }}
              onBlur={handleBlur('email')}
              variant="outlined"
              placeholder="you@example.com"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={startAdornmentSx}>
                    <EmailIcon sx={{ color: accent, fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx(accent, theme)}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (touched.password) setError('password', validatePassword(e.target.value));
              }}
              onBlur={handleBlur('password')}
              variant="outlined"
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={startAdornmentSx}>
                    <LockIcon sx={{ color: accent, fontSize: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      sx={{ color: theme.textSecondary }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx(accent, theme)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!isFormValid || submitting}
              endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                bgcolor: accent,
                color: theme.mode === 'dark' ? '#121212' : '#000',
                boxShadow: `0 4px 16px ${accent}50`,
                border: `1px solid ${accent}66`,
                '&:hover': { bgcolor: accent, boxShadow: `0 6px 20px ${accent}60` },
                '&:disabled': { opacity: 0.7 },
              }}
            >
              {submitting ? 'Creating account…' : 'Register'}
            </Button>
          </Stack>
        </form>
        {onSignInClick && (
          <Typography sx={{ mt: 2, textAlign: 'center', color: theme.textSecondary, fontSize: '0.875rem' }}>
            Already have an account?{' '}
            <Box
              component="span"
              onClick={onSignInClick}
              sx={{ color: accent, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Sign in
            </Box>
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
